import { List, Tag } from 'antd'

import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import {
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory as QmrDfmCategoryType,
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory_qmrs as QmrType,
} from '../__generated__/qmrsGroupedByDfmCategory'

import NumPartsIcon from './NumPartsIcon'
import PartRow from './PartRow'
import {
    DFM_CATEGORY_SUMMARY,
    DFM_TAG_CATEGORY_COLORS,
    DfmCategoryType,
} from './constants'

type RenderPartRowOptionsType = {
    qmr: QmrType
    isImQuoteType?: boolean
    isReleaseDfmClicked?: boolean
    refetch?: () => void
    isQuoteLocked: boolean | null | undefined
    isHighTouch?: boolean
    isNonImDfmEnabled?: boolean
} & Pick<DfmCategorySectionPropsType, 'onClickViewDfmModal'>

const RenderPartRow = (options: RenderPartRowOptionsType) => {
    const {
        onClickViewDfmModal,
        isImQuoteType,
        qmr: {
            attachmentLink,
            fileName,
            id: qmrId,
            partRevision,
            version,
            dfmFeedback,
        },
        isQuoteLocked,
        isReleaseDfmClicked,
        refetch,
        isHighTouch = false,
        isNonImDfmEnabled = false,
    } = options

    const attachmentFileName =
        attachmentLink?.quoteAttachmentRevision?.attachment?.fileName
    const thumbnailUri = partRevision?.thumbnailUri

    return (
        <PartRow
            attachmentFileName={attachmentFileName}
            dfmFeedback={dfmFeedback}
            fileName={fileName}
            onClickViewDfmModal={onClickViewDfmModal}
            qmrId={qmrId}
            isImQuoteType={isImQuoteType}
            thumbnailUri={thumbnailUri}
            version={version}
            isReleaseDfmClicked={isReleaseDfmClicked}
            refetch={refetch}
            isQuoteLocked={isQuoteLocked}
            isHighTouch={isHighTouch}
            isNonImDfmEnabled={isNonImDfmEnabled}
        />
    )
}

type DfmCategorySectionPropsType = {
    category: DfmCategoryType
    isImQuoteType?: boolean
    onClickViewDfmModal: (qmrId: string, dfmId?: string) => void
    qmrs: QmrDfmCategoryType['qmrs']
    isReleaseDfmClicked?: boolean
    refetch?: () => void
    isQuoteLocked?: boolean | null | undefined
    isHighTouch?: boolean
    isNonImDfmEnabled?: boolean
}

const DfmCategorySection = (props: DfmCategorySectionPropsType) => {
    const {
        category,
        qmrs,
        onClickViewDfmModal,
        isImQuoteType,
        isReleaseDfmClicked,
        refetch,
        isQuoteLocked,
        isHighTouch,
        isNonImDfmEnabled,
    } = props

    if (!qmrs) {
        return null
    }

    const numberOfParts =
        qmrs?.length > 1 ? `${qmrs?.length} parts` : `${qmrs?.length} part`

    return (
        <Box
            backgroundColor="white"
            border={`1px solid ${getColor('blackAlpha15')}`}
            borderRadius="4px"
            direction="col"
            marginBottom="8px"
            padding="16px"
        >
            <Box
                borderBottom={`1px solid ${getColor('blackAlpha15')}`}
                direction="row"
                justifyContent="between"
                paddingBottom="16px"
            >
                <Box direction="col">
                    <Box direction="row" width="min-content" marginBottom="4px">
                        <Text
                            color="blackAlpha85"
                            fontSize="16px"
                            fontWeight={400}
                            style={{
                                whiteSpace: 'noWrap',
                                marginRight: '8px',
                            }}
                            data-test-target="category-name"
                        >
                            {category}
                        </Text>
                        <Tag
                            color={DFM_TAG_CATEGORY_COLORS[category]}
                            data-test-target="number-of-parts"
                        >
                            <NumPartsIcon category={category} />
                            {numberOfParts}
                        </Tag>
                    </Box>
                    <Text
                        color="blackAlpha65"
                        fontSize="14px"
                        fontWeight={300}
                        data-test-target="category-summary"
                    >
                        {DFM_CATEGORY_SUMMARY[category]}
                    </Text>
                </Box>
            </Box>
            <List
                data-test-target="qmr-part-feedback-list"
                dataSource={qmrs}
                itemLayout="horizontal"
                renderItem={qmr =>
                    RenderPartRow({
                        qmr,
                        onClickViewDfmModal,
                        isImQuoteType,
                        isReleaseDfmClicked,
                        refetch,
                        isQuoteLocked,
                        isHighTouch,
                        isNonImDfmEnabled,
                    })
                }
            />
        </Box>
    )
}

export default DfmCategorySection
